import { GetStaticProps } from 'next';
import React from 'react';

import { PageInformationFieldsFragment, PressReleaseOverviewSingleFieldsFragment } from 'api/graphql-types';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import { getServerPageGetPressReleaseOverview } from 'api/graphql-helper';

import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import { Head } from 'modules/head/head';
import IntroHeader from 'modules/intro-header/intro-header';

import { initialState as settingsInitialState } from 'redux/stores/settings';
import { initialState as pressReleaseInitialState } from 'redux/stores/press-releases';

import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { ROUTES } from 'constants/routes';
import { IBasePageProps } from 'types/page';

interface IPressReleaseOverviewPageProps extends IBasePageProps {
	pressReleaseOverview?: PressReleaseOverviewSingleFieldsFragment;
	pageInformation?: PageInformationFieldsFragment;
}

const PressReleaseOverviewPage = (props: IPressReleaseOverviewPageProps): React.JSX.Element => {
	const modules = props.pressReleaseOverview?.modules;
	const seoModule = props?.pressReleaseOverview?.seo;

	return (
		<Layout navbar={props?.pageInformation?.navbar} footer={props?.pageInformation?.footer}>
			<Head seoModule={seoModule} seoParent={props?.pageInformation?.seo} />
			<IntroHeader module={props?.pressReleaseOverview?.header} slug={ROUTES.PRESS_RELEASES.fullPath} />
			<ModuleBuilder modules={modules} slug={ROUTES.PRESS_RELEASES.fullPath} />
		</Layout>
	);
};

export const getStaticProps: GetStaticProps = async (ctx) => {
	try {
		const { locale, publicationState, draftMode } = getStrapiParametersFromContext(ctx);

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const result = await getServerPageGetPressReleaseOverview(
			{
				variables: {
					locale,
					publicationState
				}
			},
			client
		);
		const {
			pageInformation,
			pressReleaseOverview,
			pressReleases_connection: pressReleasesConnection
		} = result.props.data;

		if (pressReleaseOverview == null) {
			return {
				notFound: true
			};
		}

		return {
			props: {
				pressReleaseOverview: pressReleaseOverview.data?.attributes,
				pageInformation: pageInformation?.data?.attributes,
				initialReduxState: {
					settings: {
						...settingsInitialState,
						publicationState,
						draftMode,
						contentType: 'press-release-overview',
						contentTypeId: pressReleaseOverview?.data?.documentId
					},
					pressRelease: {
						...pressReleaseInitialState,
						pressReleases: pressReleasesConnection?.data.map((item) => item.attributes),
						totalPressReleases: pressReleasesConnection?.pageInfo.total || 0
					}
				}
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in press-release overview', error);

		return {
			notFound: true
		};
	}
};

export default PressReleaseOverviewPage;
